import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";

interface ResponseDetails {
  CommercialStatus: {
    StatusCd: string;
    StatusType: string;
    Name: string;
  }[];
  CostCategory: {
    CostMetricType: any;
    CostMetricCd: string;
    Name: string;
    CostMetric: string;
  }[];
  CostMacroCategory: {
    CostMacroCategoryCd: string;
    Name: string;
  }[];
  ChargingIncrement: {
    CostIntervalCd: string;
    Name: string;
  }[];
  Tags?: {
    Name: string;
  }[];
}

interface SKUConfigData {
  isLoading: boolean;
  data: ResponseDetails | null;
  error: string | null;

  keyLoading: boolean;
  keyData: any | null;
  keyError: string | null;

  keyFetchLoading: boolean;
  keyFetchData: any | null;
  keyFetchError: string | null;
}

const initialState: SKUConfigData = {
  isLoading: true,
  data: null,
  error: null,
  keyLoading: true,
  keyData: null,
  keyError: null,
  keyFetchLoading: true,
  keyFetchData: null,
  keyFetchError: null,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchSkuConfigData = createAsyncThunk<any, void>(
  "CTDBSkuConfigSlice/fetchSkuConfigData",
  async (_, thunkApi) => {
    try {
      const response = await httpClient.get("/db/product/sku");

      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to get product sku list",
          });
        }
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: "Failed to get product sku list",
      });
    }
  },
);

export const generateKey = createAsyncThunk<any, any>(
  "CTDBSkuConfigSlice/generateKey",
  async (requestDetails, thunkApi) => {
    try {
      const response = await httpClient.post("/db/product/sku", requestDetails);

      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to generate key",
          });
        }
      }

      return response.data;
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: "Failed to generate key",
      });
    }
  },
);
export const fetchSkuApiKey = createAsyncThunk<any, any>(
  "CTDBSkuConfigSlice/fetchSkuApiKey",
  async (requestDetails, thunkApi) => {
    try {
      const response = await httpClient.post("/db/product/sku", requestDetails);

      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to fetch key",
          });
        }
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: "Failed to fetch key",
      });
    }
  },
);

const CTDBSkuConfigSlice = createSlice({
  name: "CTDBSkuConfigSlice",
  initialState,
  reducers: {
    resetSkuConfigState: (state) => {
      state.isLoading = false;
      state.data = null;
      state.error = null;
      state.keyLoading = false;
      state.keyData = null;
      state.keyError = null;
      state.keyFetchLoading = false;
      state.keyFetchData = null;
      state.keyFetchError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSkuConfigData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        fetchSkuConfigData.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          if (action.payload.Status == "success") {
            state.isLoading = false;
            state.data = action.payload.Data;
            state.error = null;
          } else if (action.payload.Status == "error") {
            state.isLoading = false;
            state.data = null;
            state.error = action.payload.message;
          }
        },
      )
      .addCase(
        fetchSkuConfigData.rejected,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.data = null;
          state.error = action.payload.message;
        },
      )
      .addCase(generateKey.pending, (state) => {
        state.keyLoading = true;
        state.keyError = null;
        state.keyData = null;
      })
      .addCase(generateKey.fulfilled, (state, action: PayloadAction<any>) => {
        if (action.payload.Status == "success") {
          state.keyLoading = false;
          state.keyData = action.payload.Data;
          state.keyError = null;
        } else if (action.payload.Status == "error") {
          state.keyLoading = false;
          state.keyData = null;
          state.keyError = action.payload.message;
        }
      })
      .addCase(
        generateKey.rejected,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.keyLoading = false;
          state.keyData = null;
          state.keyError = action.payload.message;
        },
      )
      .addCase(fetchSkuApiKey.pending, (state) => {
        state.keyFetchLoading = true;
        state.keyFetchData = null;
        state.keyFetchData = null;
      })
      .addCase(
        fetchSkuApiKey.fulfilled,
        (state, action: PayloadAction<any>) => {
          if (action.payload.Status == "success") {
            state.keyFetchLoading = false;
            state.keyFetchData = action.payload.Data;
            state.keyFetchData = null;
          } else if (action.payload.Status == "error") {
            state.keyFetchLoading = false;
            state.keyFetchData = null;
            state.keyFetchError = action.payload.message;
          }
        },
      )
      .addCase(
        fetchSkuApiKey.rejected,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.keyFetchLoading = false;
          state.keyFetchData = null;
          state.keyFetchError = action.payload.message;
        },
      );
  },
});

export const { resetSkuConfigState } = CTDBSkuConfigSlice.actions;

export default CTDBSkuConfigSlice.reducer;
